// src/pages/Employee/CreateEmployee/types.ts
// Shared types + initial state for the multi-step Create Employee wizard.
// Field names mirror the Prisma schema so the final payload maps 1:1 to the API.

export interface FileValue {
  file: File | null; // newly selected file (pending upload)
  existingUrl: string | null; // url already on the server (edit mode) or after upload
  name: string | null; // display name
}

export const emptyFile = (): FileValue => ({
  file: null,
  existingUrl: null,
  name: null,
});

// ---------- Step 1: Basic Info ----------
export interface BasicInfoData {
  name: string;
  father_name: string;
  mother_name: string;
  gender: string;
  date_of_birth: string;
  employee_code: string;
  personal_email: string;
  official_email: string;
  phone: string;
  phone_alternate: string;
  aadharr: string;
}

export const initialBasicInfo: BasicInfoData = {
  name: "",
  father_name: "",
  mother_name: "",
  gender: "",
  date_of_birth: "",
  employee_code: "",
  personal_email: "",
  official_email: "",
  phone: "",
  phone_alternate: "",
  aadharr: "",
};

// ---------- Step 2: Personal & Family ----------
export interface DependentRow {
  _key: string;
  name: string;
  age: string;
  relationship: string;
  education: string;
  working_details: string;
}

export interface PersonalFamilyData {
  marital_status: string;
  spouse_name: string;
  age: string;
  dependents: DependentRow[];
}

export const emptyDependent = (): DependentRow => ({
  _key: crypto.randomUUID(),
  name: "",
  age: "",
  relationship: "",
  education: "",
  working_details: "",
});

export const initialPersonalFamily: PersonalFamilyData = {
  marital_status: "",
  spouse_name: "",
  age: "",
  dependents: [],
};

// ---------- Step 3: Address ----------
export interface AddressData {
  present_door_no: string;
  present_street: string;
  present_village: string;
  present_taluk: string;
  present_district: string;
  present_state: string;
  present_pincode: string;

  permanent_same_as_present: boolean;
  permanent_door_no: string;
  permanent_street: string;
  permanent_village: string;
  permanent_taluk: string;
  permanent_district: string;
  permanent_state: string;
  permanent_pincode: string;

  address_proof: FileValue;
}

export const initialAddress: AddressData = {
  present_door_no: "",
  present_street: "",
  present_village: "",
  present_taluk: "",
  present_district: "",
  present_state: "",
  present_pincode: "",

  permanent_same_as_present: false,
  permanent_door_no: "",
  permanent_street: "",
  permanent_village: "",
  permanent_taluk: "",
  permanent_district: "",
  permanent_state: "",
  permanent_pincode: "",

  address_proof: emptyFile(),
};

// ---------- Step 4: Job, Salary & Bank ----------
export interface JobSalaryBankData {
  date_of_joining: string;
  designation_id: string;
  unit_id: string;
  employee_type: string;
  employee_location_ids: number[];
  nature_of_job: string;
  probation_period_months: string;
  contract_end_date: string;
  geofence: boolean;
  facedetection?: boolean;
   is_pwa?: boolean; 
  basic_salary: string;
  take_home_salary?: string;
  site_allowance?: string;
  pf_esi_applicable: boolean;
  pf_esi_gross_salary: string;
  pf_number: string;
  esi_number: string;
  paid_leaves: string;
  payment_mode: string;
  salary_calculation: string;
  overtime: boolean;

  bank_id: string;
  bank_account_no: string;
  ifsc_code: string;
  bank_branch: string;
}

export const initialJobSalaryBank: JobSalaryBankData = {
  date_of_joining: "",
  designation_id: "",
  unit_id: "",
  employee_type: "",
  employee_location_ids: [],
  nature_of_job: "",
  probation_period_months: "6",
  contract_end_date: "",
  geofence: true,
   is_pwa: false, 
  basic_salary: "",
  pf_esi_applicable: false,
  pf_esi_gross_salary: "",
  pf_number: "",
  esi_number: "",
  paid_leaves: "",
  payment_mode: "",
  salary_calculation: "",
  overtime: false,

  bank_id: "",
  bank_account_no: "",
  ifsc_code: "",
  bank_branch: "",
};

// ---------- Step 5: Education / Experience / Certifications ----------
export interface EducationRow {
  _key: string;
  course_type: string;
  course_name: string;
  board_or_school: string;
  percentage: string;
  from_year: string;
  to_year: string;
  duration_months: string;
  institution: string;
}

export const emptyEducation = (): EducationRow => ({
  _key: crypto.randomUUID(),
  course_type: "",
  course_name: "",
  board_or_school: "",
  percentage: "",
  from_year: "",
  to_year: "",
  duration_months: "",
  institution: "",
});

export interface ExperienceRow {
  _key: string;
  company_name: string;
  from_date: string;
  to_date: string;
  nature_of_work: string;
  reason_for_leaving: string;
  attachment: FileValue;
}

export const emptyExperience = (): ExperienceRow => ({
  _key: crypto.randomUUID(),
  company_name: "",
  from_date: "",
  to_date: "",
  nature_of_work: "",
  reason_for_leaving: "",
  attachment: emptyFile(),
});

export interface CertificationRow {
  _key: string;
  course_name: string;
  duration_months: string;
  percentage: string;
  institution: string;
  attachment: FileValue;
}

export const emptyCertification = (): CertificationRow => ({
  _key: crypto.randomUUID(),
  course_name: "",
  duration_months: "",
  percentage: "",
  institution: "",
  attachment: emptyFile(),
});

export interface QualificationsData {
  educations: EducationRow[];
  experiences: ExperienceRow[];
  certifications: CertificationRow[];
}

export const initialQualifications: QualificationsData = {
  educations: [],
  experiences: [],
  certifications: [],
};

// ---------- Step 6: Documents ----------
export interface DocumentsData {
  pan_number: string;
  pan_attachment: FileValue;
  aadhar_attachment: FileValue;
  epf_uan: string;
  epf_attachment: FileValue;
  esic_number: string;
  esic_attachment: FileValue;
  resume: FileValue;
  payslip_1: FileValue;
  payslip_2: FileValue;
  payslip_3: FileValue;
}

export const initialDocuments: DocumentsData = {
  pan_number: "",
  pan_attachment: emptyFile(),
  aadhar_attachment: emptyFile(),
  epf_uan: "",
  epf_attachment: emptyFile(),
  esic_number: "",
  esic_attachment: emptyFile(),
  resume: emptyFile(),
  payslip_1: emptyFile(),
  payslip_2: emptyFile(),
  payslip_3: emptyFile(),
};

// ---------- Step 7: Emergency Contact & Reference ----------
export interface EmergencyReferenceData {
  emergency_name: string;
  emergency_relationship: string;
  emergency_mobile: string;
  emergency_email: string;

  reference_name: string;
  reference_designation: string;
  reference_address: string;
  reference_mobile: string;
  reference_email: string;
}

export const initialEmergencyReference: EmergencyReferenceData = {
  emergency_name: "",
  emergency_relationship: "",
  emergency_mobile: "",
  emergency_email: "",

  reference_name: "",
  reference_designation: "",
  reference_address: "",
  reference_mobile: "",
  reference_email: "",
};

// ---------- Combined wizard state ----------
export interface EmployeeFormData {
  basicInfo: BasicInfoData;
  personalFamily: PersonalFamilyData;
  address: AddressData;
  jobSalaryBank: JobSalaryBankData;
  qualifications: QualificationsData;
  documents: DocumentsData;
  emergencyReference: EmergencyReferenceData;
}

export const initialEmployeeFormData: EmployeeFormData = {
  basicInfo: initialBasicInfo,
  personalFamily: initialPersonalFamily,
  address: initialAddress,
  jobSalaryBank: initialJobSalaryBank,
  qualifications: initialQualifications,
  documents: initialDocuments,
  emergencyReference: initialEmergencyReference,
};

// Errors are a flat map keyed by field name (per step, cleared per step)
export type StepErrors = Record<string, string>;

export interface StepProps<T> {
  data: T;
  onChange: (data: T) => void;
  errors: StepErrors;
  onValidationChange?: (isValid: boolean) => void;
  formData?: any;
}

export interface WizardStepMeta {
  key: string;
  label: string;
  description: string;
}

export const WIZARD_STEPS: WizardStepMeta[] = [
  { key: "basic", label: "Basic Info", description: "Identity & contact" },
  {
    key: "personal",
    label: "Personal & Family",
    description: "Marital status, dependents",
  },
  { key: "address", label: "Address", description: "Present & permanent" },
  {
    key: "job",
    label: "Job, Salary & Bank",
    description: "Role, pay, bank details",
  },
  {
    key: "qualifications",
    label: "Qualifications",
    description: "Education, experience, certificates",
  },
  { key: "documents", label: "Documents", description: "ID proofs & files" },
  {
    key: "emergency",
    label: "Emergency & Reference",
    description: "Contacts on file",
  },
];
